import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SEO, { buildFaqJsonLd } from "@/components/SEO";
import BreadcrumbNav from "@/components/BreadcrumbNav";
import CTASection from "@/components/CTASection";
import { BLOG_POSTS } from "@/data/blog";
import { slugToTitle } from "@/data/services";

const BLOG_INDEX_BREADCRUMBS = [{ label: "Home", to: "/" }, { label: "Resources" }];

export function BlogIndex() {
  // Group posts by category for nicer SEO browsing
  const categories = Array.from(new Set(BLOG_POSTS.map((p) => p.category)));

  return (
    <>
      <SEO title="Blog & Resources | Black Ridge Construction" description="Practical guides and resources for Austin homeowners — roofing, remodeling, maintenance, storm prep, investor properties, and more." />
      <section className="bg-br-black text-white">
        <div className="container-br py-12 lg:py-16">
          <BreadcrumbNav dark items={BLOG_INDEX_BREADCRUMBS} />
          <div className="max-w-3xl mt-5">
            <div className="br-eyebrow text-br-bronze-2">Resources</div>
            <h1 className="br-headline text-4xl sm:text-5xl mt-3">Helpful Reading for Austin Homeowners</h1>
            <p className="mt-4 text-gray-300">Practical articles on roofing, remodeling, maintenance, storm prep, and investor property care from a working Austin general contractor.</p>
          </div>
        </div>
      </section>

      <section className="section-pad bg-br-paper">
        <div className="container-br">
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map((c) => (
              <span key={c} className="text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-700">{c}</span>
            ))}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {BLOG_POSTS.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group br-card overflow-hidden bg-white">
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" loading="lazy"/>
                </div>
                <div className="p-5">
                  <div className="br-eyebrow text-br-bronze">{p.category}</div>
                  <h3 className="font-display text-lg tracking-wider uppercase mt-1">{p.title}</h3>
                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">{p.excerpt}</p>
                  <div className="mt-3 text-xs uppercase tracking-widest font-bold text-br-bronze">Read article →</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  const postTitle = post?.title || "";

  // Hooks must run unconditionally — declare before the early return below.
  const breadcrumbs = useMemo(
    () => [{ label: "Home", to: "/" }, { label: "Resources", to: "/blog" }, { label: postTitle }],
    [postTitle]
  );

  if (!post) {
    return (
      <>
        <SEO title="Article Not Found" noIndex />
        <section className="section-pad">
          <div className="container-br">
            <h1 className="br-headline text-3xl">Article not found</h1>
            <p className="mt-3 text-gray-600">Browse our <Link className="text-br-bronze underline" to="/blog">blog index</Link>.</p>
          </div>
        </section>
      </>
    );
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    author: { "@type": "Organization", name: "Black Ridge Construction" },
    publisher: {
      "@type": "Organization",
      name: "Black Ridge Construction",
      logo: { "@type": "ImageObject", url: "https://ridge-roofing-austin.preview.emergentagent.com/favicon.ico" },
    },
    keywords: (post.keywords || []).join(", "),
  };

  const combinedJsonLd = post.faqs && post.faqs.length
    ? { "@context": "https://schema.org", "@graph": [articleJsonLd, buildFaqJsonLd(post.faqs)] }
    : articleJsonLd;

  return (
    <>
      <SEO
        title={`${post.metaTitle || post.title} | Black Ridge Construction`}
        description={post.metaDescription || post.excerpt}
        ogImage={post.image}
        jsonLd={combinedJsonLd}
      />
      <article className="section-pad bg-white">
        <div className="container-br max-w-4xl">
          <BreadcrumbNav items={breadcrumbs} />
          <div className="br-eyebrow text-br-bronze mt-5">{post.category}</div>
          <h1 className="br-headline text-3xl sm:text-4xl mt-3">{post.title}</h1>
          <p className="mt-3 text-gray-700 text-lg leading-relaxed">{post.excerpt}</p>
          <div className="mt-6 aspect-[16/8] overflow-hidden rounded">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover"/>
          </div>

          {/* Structured sections */}
          <div className="prose-br mt-8 space-y-8 text-gray-800 leading-relaxed">
            {(post.sections || []).map((sec) => (
              <section key={sec.h2}>
                <h2 className="font-display tracking-widest uppercase text-xl text-gray-900 mb-3">{sec.h2}</h2>
                {(sec.body || []).map((para) => (
                  <p key={para.slice(0, 80)} className="mb-3 text-[15px]">{para}</p>
                ))}
                {sec.list && sec.list.length > 0 && (
                  <ul className="space-y-2 mt-2">
                    {sec.list.map((li) => (
                      <li key={li} className="flex gap-2 text-[15px]">
                        <span className="text-br-bronze shrink-0">▪</span>
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          {/* FAQs */}
          {post.faqs && post.faqs.length > 0 && (
            <div className="mt-10 bg-br-paper p-6 rounded border border-gray-200">
              <h2 className="font-display tracking-widest uppercase text-xl mb-4">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {post.faqs.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-display tracking-wider uppercase text-sm text-gray-900">{f.q}</h3>
                    <p className="mt-1 text-sm text-gray-700 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Related services */}
          {post.related && post.related.length > 0 && (
            <div className="mt-10">
              <div className="br-eyebrow text-br-bronze mb-2">Related Services</div>
              <div className="flex flex-wrap gap-2">
                {post.related.map((s) => (
                  <Link key={s} to={`/services/${s}`} className="px-3 py-1.5 text-xs uppercase tracking-widest rounded border border-gray-200 hover:border-br-bronze hover:text-br-bronze transition">
                    {slugToTitle(s)}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Inline CTA */}
          <div className="mt-10 p-5 bg-br-black text-white rounded flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-display tracking-widest uppercase">Have a question about your home?</div>
              <div className="text-sm text-gray-300">Request a consultation — we respond promptly.</div>
            </div>
            <Link to="/contact" className="br-btn-primary">Request a Consultation</Link>
          </div>
        </div>
      </article>
      <CTASection />
    </>
  );
}
